// Client-side validation for the brand-voice document dropzone (AI-90).
//
// Mirrors the nexus server floor `AcceptUpload::MAX_SOURCE_UPLOAD_BYTES =
// 50.megabytes` (Decision 2): a presigned S3 PUT cannot cap content length
// itself, so the FE enforces the same ceiling before uploading.
export const MAX_BRAND_VOICE_UPLOAD_BYTES = 50 * 1024 * 1024

// Accepted document types — PDF/DOCX/TXT/MD/CSV. Q12: ".doc" dropped because
// thanx-ai's FileIngestor has no OLE2 branch, so a legacy .doc silently reads
// as unreadable and returns "couldn't derive" — better not to offer a type we
// cannot read. ".md"/".csv" (AI-1120) are safe to add: nexus has no
// server-side type gate, and thanx-ai's FileIngestor already treats printable
// non-PDF/DOCX bytes as text, so both read the same way ".txt" does.
export const ACCEPTED_BRAND_VOICE_EXTENSIONS = [
  '.pdf',
  '.docx',
  '.txt',
  '.md',
  '.csv',
] as const

// Secondary MIME allow-list for the drag path, where the OS supplies file.type
// but the filename may be sparse. Extension is the primary/authoritative signal
// (drag-drop MIME is unreliable across OSes — see the plan risk register); MIME
// only accepts an otherwise-unrecognised file, never overrides a bad extension.
const ACCEPTED_BRAND_VOICE_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'text/plain',
  'text/markdown', // .md
  'text/csv', // .csv
]

export type BrandVoiceFileError = 'type' | 'size'

// Returns null when the file is an accepted brand-voice document within the size
// ceiling, else the reason it was rejected. Type is checked before size so an
// unsupported oversize file reports 'type' (the actionable fix), matching the copy.
export function validateBrandVoiceFile(file: File): BrandVoiceFileError | null {
  if (!file) return 'type'

  const name = file.name?.toLowerCase() ?? ''
  const hasAcceptedExtension = ACCEPTED_BRAND_VOICE_EXTENSIONS.some(ext =>
    name.endsWith(ext)
  )
  const hasAcceptedMime = ACCEPTED_BRAND_VOICE_MIME_TYPES.includes(file.type)
  if (!hasAcceptedExtension && !hasAcceptedMime) return 'type'

  if (file.size > MAX_BRAND_VOICE_UPLOAD_BYTES) return 'size'

  return null
}
